import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import roleoAutomaticoIllustration from '../../../../assets/images/roleo-automatico.svg';
import { getTribunalesArea } from '../../../../services/roleoAutomaticoService';
import { useLanguage } from '../../../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../../../utils/createScopedI18n';
import { roleoAutomaticoI18n } from './i18n';

const getSortValue = (row, key) => {
  switch (key) {
    case 'name':
      return row.name || '';
    case 'region':
      return row.region || '';
    case 'type':
      return row.type || '';
    default:
      return '';
  }
};

const RoleoAutomaticoTable = memo(function RoleoAutomaticoTable({
  Table,
  t,
  headerCheckboxRef,
  allFilteredSelected,
  filteredIdsLength,
  toggleSelectAllFiltered,
  selectedIdSet,
  toggleTribunal,
  sortedTribunals,
  sortKey,
  sortDirection,
  onServerSortChange,
  loadingTribunals,
  selectedCount,
}) {
  const renderSelectableText = useCallback(
    (row, value) => {
      const isSelected = selectedIdSet.has(row.id);
      return (
        <button
          type='button'
          className={`roleo-modal__row-button${
            isSelected ? ' is-selected' : ''
          }`}
          onClick={() => toggleTribunal(row.id)}
          aria-pressed={isSelected}
        >
          {value || '-'}
        </button>
      );
    },
    [selectedIdSet, toggleTribunal]
  );

  const columns = useMemo(
    () => [
      {
        label: (
          <input
            ref={headerCheckboxRef}
            type='checkbox'
            aria-label={t('roleo_auto_modal_select_all_aria')}
            checked={allFilteredSelected}
            onChange={toggleSelectAllFiltered}
            disabled={filteredIdsLength === 0}
          />
        ),
        width: '48px',
        render: (row) => {
          const isSelected = selectedIdSet.has(row.id);
          return (
            <div
              className={`roleo-modal__row-cell roleo-modal__row-cell--checkbox${
                isSelected ? ' is-selected' : ''
              }`}
              onClick={() => toggleTribunal(row.id)}
            >
              <input
                type='checkbox'
                aria-label={t('roleo_auto_modal_select_one_aria', {
                  name: row.name || '-',
                })}
                checked={isSelected}
                onClick={(event) => event.stopPropagation()}
                onChange={() => toggleTribunal(row.id)}
              />
            </div>
          );
        },
      },
      {
        accessor: 'name',
        label: t('roleo_auto_modal_col_court'),
        sort: true,
        render: (row) => renderSelectableText(row, row.name),
      },
      {
        accessor: 'region',
        label: t('roleo_auto_modal_col_region'),
        sort: true,
        render: (row) => renderSelectableText(row, row.region),
      },
    ],
    [
      t,
      headerCheckboxRef,
      allFilteredSelected,
      filteredIdsLength,
      toggleSelectAllFiltered,
      renderSelectableText,
      selectedIdSet,
      toggleTribunal,
    ]
  );

  return (
    <div className='table-container roleo-modal__table-results'>
      <Table
        columns={columns}
        data={sortedTribunals}
        serverSortKey={sortKey}
        serverSortDirection={sortDirection}
        onServerSortChange={onServerSortChange}
        loading={loadingTribunals}
      />
      <div className='roleo-modal__footer'>
        <span className='caption font-light fw-semibold'>
          {t('roleo_auto_modal_selected_count', { count: selectedCount })}
        </span>
      </div>
    </div>
  );
});

export default function RoleoAutomaticoModal({
  Modal,
  Input,
  Dropdown,
  Table,
  Button,
  IconButton,
  ComboBox,
  show,
  onClose,
  user,
  SvgIcon,
  areaOptions = [],
  tribunals = [],
  loadingTribunals = false,
  regions = [],
  loadingRegions = false,
  onSave,
  onAssignArea,
}) {
  const { language } = useLanguage();
  const roleoLanguage = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: roleoAutomaticoI18n });
  const navigate = useNavigate();
  const [filterType, setFilterType] = useState('case');
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('all');
  const [regionFilter, setRegionFilter] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectionTouched, setSelectionTouched] = useState(false);
  const [sortKey, setSortKey] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);
  const [areaTribunals, setAreaTribunals] = useState([]);
  const [areaName, setAreaName] = useState('');
  const [areaLoading, setAreaLoading] = useState(false);
  const [showAreaModal, setShowAreaModal] = useState(false);
  const [selectedAreaOption, setSelectedAreaOption] = useState(null);
  const headerCheckboxRef = useRef(null);
  const hydratedRef = useRef(false);

  useEffect(() => {
    roleoAutomaticoI18n.changeLanguage(roleoLanguage);
  }, [roleoLanguage]);

  const tabOptions = useMemo(
    () => [
      { label: t('roleo_auto_modal_tab_all'), value: 'all' },
      { label: t('roleo_auto_modal_tab_assigned', { count: 0 }), value: 'assigned' },
      { label: t('roleo_auto_modal_tab_unassigned'), value: 'unassigned' },
    ],
    [t]
  );

  const regionOptions = useMemo(() => {
    const values = regions
      .map((region) => region.alias || region.name || region.label || '')
      .filter(Boolean)
      .sort();
    return [
      { label: t('roleo_auto_modal_all_regions'), value: 'all' },
      ...values.map((region) => ({ label: region, value: region })),
    ];
  }, [regions, t]);

  const filterOptionFrom = (options, value) =>
    options.find((opt) => opt.value === value) ?? options[0] ?? null;
  const areaAssignOptions = useMemo(
    () => areaOptions.filter((opt) => opt.value !== 'all'),
    [areaOptions]
  );

  useEffect(() => {
    if (!show) {
      hydratedRef.current = false;
      return;
    }
    if (hydratedRef.current) return;

    const initialFilter =
      user?.filterType === 'court' || user?.filterType === 'area'
        ? user.filterType
        : 'case';
    setFilterType(initialFilter);
    setSearch('');
    setTab('all');
    setRegionFilter(regionOptions[0] ?? null);
    setSortKey(null);
    setSortDirection(null);
    setSelectedIds([]);
    setSelectionTouched(false);
    setAreaTribunals([]);
    setAreaName('');
    setShowAreaModal(false);
    setSelectedAreaOption(null);

    hydratedRef.current = true;
  }, [show, user, regionOptions]);

  useEffect(() => {
    if (!show) return;
    const currentValue = regionFilter?.value ?? 'all';
    if (!regionOptions.some((opt) => opt.value === currentValue)) {
      setRegionFilter(regionOptions[0] ?? null);
    }
  }, [show, regionOptions, regionFilter]);

  useEffect(() => {
    if (!show) return;
    if (filterType !== 'court') return;
    if (selectionTouched) return;
    if (selectedIds.length > 0) return;
    if (tribunals.length === 0) return;

    const userTribunalIds = Array.isArray(user?.tribunalIds)
      ? user.tribunalIds
      : [];

    if (userTribunalIds.length > 0) {
      setSelectedIds(userTribunalIds);
      return;
    }

    if (!user?.courts?.length) return;

    const preselected = tribunals
      .filter((tribunal) => user.courts.includes(tribunal.name))
      .map((tribunal) => tribunal.id);

    if (preselected.length > 0) {
      setSelectedIds(preselected);
    }
  }, [show, filterType, user, selectionTouched, selectedIds.length, tribunals]);

  useEffect(() => {
    if (!show) return;
    if (filterType !== 'area') return;
    const businessAreaId = user?.businessAreaId ?? null;
    if (!businessAreaId) {
      setAreaTribunals([]);
      setAreaName('');
      return;
    }
    let mounted = true;

    const fetchAreaTribunals = async () => {
      try {
        setAreaLoading(true);
        const resp = await getTribunalesArea(businessAreaId);
        if (!mounted) return;
        const courts = Array.isArray(resp?.courts) ? resp.courts : [];
        setAreaTribunals(
          courts
            .map((court) => court.nombre || court.name || '')
            .filter(Boolean)
        );
        setAreaName(resp?.businessAreaName || '');
      } catch (error) {
        console.error('Error loading area courts:', error);
        if (mounted) {
          setAreaTribunals([]);
          setAreaName('');
        }
      } finally {
        if (mounted) {
          setAreaLoading(false);
        }
      }
    };

    fetchAreaTribunals();

    return () => {
      mounted = false;
    };
  }, [show, filterType, user]);

  useEffect(() => {
    if (!showAreaModal) return;
    const currentId = user?.businessAreaId ?? null;
    const match =
      areaAssignOptions.find((opt) => opt.value === currentId) ?? null;
    setSelectedAreaOption(match);
  }, [showAreaModal, areaAssignOptions, user]);

  const assignedCount = selectedIds.length;

  const normalizedSearch = search.trim().toLowerCase();
  const filteredTribunals = useMemo(() => {
    return tribunals.filter((tribunal) => {
      if (regionFilter?.value && regionFilter.value !== 'all') {
        if (tribunal.region !== regionFilter.value) return false;
      }

      const isAssigned = selectedIds.includes(tribunal.id);
      if (tab === 'assigned' && !isAssigned) return false;
      if (tab === 'unassigned' && isAssigned) return false;

      if (!normalizedSearch) return true;
      const haystack = `${tribunal.name || ''} ${tribunal.region || ''} ${
        tribunal.type || ''
      }`
        .toLowerCase()
        .trim();
      return haystack.includes(normalizedSearch);
    });
  }, [tribunals, normalizedSearch, regionFilter, tab, selectedIds]);

  const filteredIds = useMemo(
    () => filteredTribunals.map((tribunal) => tribunal.id),
    [filteredTribunals]
  );
  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const allFilteredSelected =
    filteredIds.length > 0 &&
    filteredIds.every((id) => selectedIds.includes(id));
  const someFilteredSelected =
    filteredIds.some((id) => selectedIds.includes(id)) && !allFilteredSelected;

  useEffect(() => {
    if (!headerCheckboxRef.current) return;
    headerCheckboxRef.current.indeterminate = someFilteredSelected;
  }, [someFilteredSelected]);

  const sortedTribunals = useMemo(() => {
    const data = [...filteredTribunals];
    if (!sortKey || !sortDirection) return data;

    const direction = sortDirection === 'desc' ? -1 : 1;
    data.sort((a, b) => {
      const aValue = getSortValue(a, sortKey);
      const bValue = getSortValue(b, sortKey);
      return (
        aValue.localeCompare(bValue, 'es', {
          sensitivity: 'base',
          numeric: true,
        }) * direction
      );
    });
    return data;
  }, [filteredTribunals, sortKey, sortDirection]);

  const handleServerSortChange = useCallback(({ sortKey: key, direction }) => {
    const normalizedDirection =
      typeof direction === 'string' ? direction.toLowerCase() : direction;
    setSortKey(normalizedDirection ? key : null);
    setSortDirection(normalizedDirection ?? null);
  }, []);

  const toggleTribunal = useCallback((id) => {
    setSelectionTouched(true);
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  }, []);

  const toggleSelectAllFiltered = useCallback(() => {
    setSelectionTouched(true);
    setSelectedIds((prev) => {
      if (filteredIds.length === 0) return prev;

      if (allFilteredSelected) {
        return prev.filter((id) => !filteredIds.includes(id));
      }

      const next = new Set(prev);
      filteredIds.forEach((id) => next.add(id));
      return Array.from(next);
    });
  }, [allFilteredSelected, filteredIds]);

  const isAreaInvalid =
    filterType === 'area' &&
    (!user?.businessAreaId || areaLoading || areaTribunals.length === 0);
  const isSaveDisabled =
    (filterType === 'court' && selectedIds.length === 0) || isAreaInvalid;

  const handleSave = () => {
    const payload = {
      userid: user?.id,
      tipoBusqueda:
        filterType === 'case'
          ? 'caso'
          : filterType === 'area'
            ? 'area'
            : 'tribunal',
      tribunales: filterType === 'court' ? selectedIds : [],
    };

    onSave?.(payload);
  };

  useEffect(() => {
    const updateTableHeight = () => {
      const h = window.innerHeight;
      let maxHeight = 498;

      if (h < 600) {
        maxHeight = 41;
      } else if (h < 700) {
        maxHeight = 59;
      } else if (h < 800) {
        maxHeight = 71;
      } else if (h < 900) {
        maxHeight = 75;
      } else if (h < 1000) {
        maxHeight = 78;
      } else if (h < 1100) {
        maxHeight = 90;
      }

      document.documentElement.style.setProperty(
        '--table-max-height',
        `${maxHeight}%`
      );
    };

    updateTableHeight();
    window.addEventListener('resize', updateTableHeight);

    return () => window.removeEventListener('resize', updateTableHeight);
  }, []);

  if (!Modal || !Input || !Dropdown || !Table || !ComboBox) return null;
  if (!show) return null;

  const modalWidth = filterType === 'court' ? '70vw' : '760px';
  const contentClassName = `roleo-modal__content ${
    filterType === 'court' ? 'is-court' : 'is-case'
  }`;
  const handleConfigureArea = () => {
    const searchValue = areaName || user?.businessAreaName || '';
    navigate('/configuracion/gestion-datos/areas', {
      state: searchValue ? { search: searchValue } : undefined,
    });
  };

  return (
    <>
      <Modal
        title={t('roleo_auto_modal_title')}
        show={show}
        onClose={onClose}
        primaryBtnTxt={t('roleo_auto_modal_save')}
        primaryBtnAction={handleSave}
        disabledPrimaryBtn={isSaveDisabled}
        secondaryBtnTxt={t('roleo_auto_modal_cancel')}
        secondaryBtnAction={onClose}
        width={modalWidth}
      >
        <div className={contentClassName}>
          <div className='roleo-modal__filters'>
            <div className='roleo-modal__section'>
              <p className='roleo-modal__subtitle mb-4'>{user?.user || '-'}</p>
              <div className='d-flex align-items-center gap-2 mb-2'>
                <span className='fw-semibold'>
                  {t('roleo_auto_modal_filter_title')}
                </span>
                <span className='text-danger'>*</span>
              </div>
              <div className='roleo-modal__radio-group'>
                <label
                  className={`roleo-modal__radio ${
                    filterType === 'case' ? 'is-active' : ''
                  }`}
                >
                  <input
                    type='radio'
                    name='roleo-filter'
                    checked={filterType === 'case'}
                    onChange={() => setFilterType('case')}
                  />
                  <div>
                    <div className='roleo-modal__radio-title'>
                      {t('roleo_auto_modal_case_title')}
                    </div>
                    <div className='roleo-modal__radio-description'>
                      {t('roleo_auto_modal_case_desc')}
                    </div>
                  </div>
                </label>
                <label
                  className={`roleo-modal__radio ${
                    filterType === 'court' ? 'is-active' : ''
                  }`}
                >
                  <input
                    type='radio'
                    name='roleo-filter'
                    checked={filterType === 'court'}
                    onChange={() => setFilterType('court')}
                  />
                  <div>
                    <div className='roleo-modal__radio-title'>
                      {t('roleo_auto_modal_court_title')}
                    </div>
                    <div className='roleo-modal__radio-description'>
                      {t('roleo_auto_modal_court_desc')}
                    </div>
                  </div>
                </label>
                <label
                  className={`roleo-modal__radio ${
                    filterType === 'area' ? 'is-active' : ''
                  }`}
                >
                  <input
                    type='radio'
                    name='roleo-filter'
                    checked={filterType === 'area'}
                    onChange={() => setFilterType('area')}
                  />
                  <div>
                    <div className='roleo-modal__radio-title'>
                      {t('roleo_auto_modal_area_title')}
                    </div>
                    <div className='roleo-modal__radio-description'>
                      {t('roleo_auto_modal_area_desc')}
                    </div>
                  </div>
                </label>
              </div>
            </div>

            <div className='roleo-modal__bottom-card'>
              <div className='message-info'>
                <SvgIcon
                  name='infoCircle'
                  fill='var(--primary-600)'
                  size='20px'
                />
                <p className='mb-0'>
                  {filterType === 'case'
                    ? t('roleo_auto_modal_info_case')
                    : filterType === 'area'
                      ? t('roleo_auto_modal_info_area')
                      : t('roleo_auto_modal_info_court')}
                </p>
              </div>
            </div>
          </div>

          <div className='roleo-modal__panels'>
            {filterType === 'court' ? (
              <div className='roleo-modal__panel roleo-modal__results'>
                <div className='roleo-modal__table-filters'>
                  <div className='w-100'>
                    <Input
                      icon='search'
                      name='searchTribunal'
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder={t('roleo_auto_modal_search_court')}
                      type='text'
                      value={search}
                    />
                  </div>
                  <div className='w-100'>
                    <ComboBox
                      options={regionOptions}
                      value={filterOptionFrom(regionOptions, regionFilter?.value)}
                      onChange={(opt) => setRegionFilter(opt)}
                      placeholder={t('roleo_auto_modal_all_regions')}
                      disabled={loadingRegions || regionOptions.length <= 1}
                    />
                  </div>
                </div>
                <nav className='roleo-tabs' role='tablist'>
                  {tabOptions.map((option) => {
                    const label =
                      option.value === 'assigned'
                        ? t('roleo_auto_modal_tab_assigned', {
                            count: assignedCount,
                          })
                        : option.label;
                    return (
                      <button
                        key={option.value}
                        type='button'
                        className={
                          option.value === tab
                            ? 'roleo-tab is-active'
                            : 'roleo-tab'
                        }
                        onClick={() => setTab(option.value)}
                      >
                        {label}
                      </button>
                    );
                  })}
                </nav>

                <RoleoAutomaticoTable
                  Table={Table}
                  t={t}
                  headerCheckboxRef={headerCheckboxRef}
                  allFilteredSelected={allFilteredSelected}
                  filteredIdsLength={filteredIds.length}
                  toggleSelectAllFiltered={toggleSelectAllFiltered}
                  selectedIdSet={selectedIdSet}
                  toggleTribunal={toggleTribunal}
                  sortedTribunals={sortedTribunals}
                  sortKey={sortKey}
                  sortDirection={sortDirection}
                  onServerSortChange={handleServerSortChange}
                  loadingTribunals={loadingTribunals}
                  selectedCount={selectedIds.length}
                />
              </div>
            ) : filterType === 'area' ? (
              <div className='roleo-modal__panel roleo-modal__area-info'>
                <div className='roleo-modal__area-header'>
                  <h3 className='fw-semibold mb-0'>
                    {areaName ||
                      user?.businessAreaName ||
                      t('roleo_auto_modal_user_without_area')}
                  </h3>
                  {IconButton ? (
                    <IconButton
                      ariaLabel={t('roleo_auto_modal_edit_user_area')}
                      icon='pencil'
                      onClick={() => setShowAreaModal(true)}
                      size='sm'
                      iconStroke='var(--primary-500)'
                    />
                  ) : (
                    <Button
                      label={t('roleo_auto_disclaimer_configure_areas')}
                      variant='naked'
                      size='sm'
                      onClick={() => setShowAreaModal(true)}
                    />
                  )}
                </div>
                {areaLoading ? (
                  <p className='caption font-light'>
                    {t('roleo_auto_modal_loading_area_courts')}
                  </p>
                ) : areaTribunals.length === 0 ? (
                  user?.businessAreaId ? (
                    <div className='d-flex flex-column gap-2'>
                      <p className='caption font-light mb-0'>
                        {t('roleo_auto_modal_area_requires_courts')}
                      </p>
                      <Button
                        label={t('roleo_auto_modal_configure_area')}
                        variant='naked'
                        size='sm'
                        onClick={handleConfigureArea}
                      />
                    </div>
                  ) : (
                    <p className='caption font-light'>
                      {t('roleo_auto_modal_user_without_area_message')}
                    </p>
                  )
                ) : (
                  <ul className='roleo-modal__area-list mini'>
                    {areaTribunals.map((court) => (
                      <li key={court} className='mb-1'>
                        {court}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ) : (
              <div className='roleo-modal__panel roleo-modal__illustration'>
                <img
                  src={roleoAutomaticoIllustration}
                  alt=''
                  className='roleo-modal__illustration-img'
                />
              </div>
            )}
          </div>
        </div>
      </Modal>
      <Modal
        title={t('roleo_auto_modal_assign_area_title')}
        show={showAreaModal}
        onClose={() => setShowAreaModal(false)}
        primaryBtnTxt={t('roleo_auto_modal_assign_area_confirm')}
        primaryBtnAction={() => {
          if (!selectedAreaOption) return;
          onAssignArea?.(selectedAreaOption.value);
          setShowAreaModal(false);
        }}
        disabledPrimaryBtn={!selectedAreaOption}
        secondaryBtnTxt={t('roleo_auto_modal_cancel')}
        secondaryBtnAction={() => setShowAreaModal(false)}
        width='520px'
      >
        <div className='d-flex flex-column gap-3'>
          <p className='caption font-light mb-0'>
            {t('roleo_auto_modal_assign_area_body', {
              user: user?.user || '-',
            })}
          </p>
          <ComboBox
            label={t('roleo_auto_modal_assign_area_label')}
            options={areaAssignOptions}
            value={selectedAreaOption}
            onChange={(opt) => setSelectedAreaOption(opt)}
            placeholder={t('roleo_auto_modal_assign_area_placeholder')}
          />
        </div>
      </Modal>
    </>
  );
}
